/* eslint-disable consistent-return, no-param-reassign */
import { type Ref, ref } from 'vue'

import { useEventListener, useRafFn } from '@vueuse/core'

interface DraggableRailOptions {
  /** px of travel before a press counts as a drag, not a click. */
  threshold?: number
  /** px/ms release speed below which we don't glide at all. */
  flickMin?: number
  /** px/ms at which a decaying glide has slowed enough to stop. */
  glideMin?: number
  /** per-frame momentum decay. */
  friction?: number
}

/**
 * Free-scroll drag ("free mode", à la Swiper) for a horizontal rail — no snap,
 * and a flick carries into a decaying glide so it feels like native touch
 * momentum. Touch scrolls the rail natively (the per-panel Lenis leaves touch
 * alone) and shift+wheel works on `overflow-x-auto` out of the box, so this only
 * wires up pointer dragging. It also swallows the trailing click after a drag so
 * a drag never activates a card. Bind the rail via `ref` and pass that ref in;
 * read `dragging` for a `[data-dragging]` grab/grabbing cursor hook.
 */
export function useDraggableRail(
  track: Ref<HTMLElement | null>,
  options: DraggableRailOptions = {},
) {
  const {
    threshold = 5,
    flickMin = 0.1,
    glideMin = 0.02,
    friction = 0.94,
  } = options

  const dragging = ref(false)

  let active = false
  let startX = 0
  let startScroll = 0
  // True once a press crosses the threshold; kept until the trailing click is
  // swallowed (or the next press) so a drag never activates a card.
  let moved = false
  // Scroll speed (px/ms, opposite the pointer) sampled during the drag; the
  // momentum loop decays it after release.
  let momentum = 0
  let lastX = 0
  let lastT = 0

  const { pause: stopMomentum, resume: startMomentum } = useRafFn(
    ({ delta }) => {
      const el = track.value
      if (!el) return stopMomentum()
      el.scrollLeft += momentum * delta
      momentum *= friction
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1
      if (Math.abs(momentum) < glideMin || el.scrollLeft <= 0 || atEnd)
        stopMomentum()
    },
    { immediate: false },
  )

  function onDown(event: PointerEvent) {
    if (event.pointerType === 'touch' || !track.value) return
    stopMomentum()
    active = true
    moved = false
    momentum = 0
    startX = event.clientX
    startScroll = track.value.scrollLeft
    lastX = event.clientX
    lastT = event.timeStamp
  }

  function onMove(event: PointerEvent) {
    if (!active || !track.value) return
    const dx = event.clientX - startX
    // Ignore sub-threshold jitter so a plain click isn't read as a drag.
    if (!dragging.value && Math.abs(dx) < threshold) return
    dragging.value = true
    moved = true
    track.value.scrollLeft = startScroll - dx
    // Sample scroll velocity (opposite the pointer) for the release glide.
    const dt = event.timeStamp - lastT
    if (dt > 0) momentum = -(event.clientX - lastX) / dt
    lastX = event.clientX
    lastT = event.timeStamp
    event.preventDefault()
  }

  function onUp() {
    if (!active) return
    active = false
    dragging.value = false
    // Let a real flick coast; a slow release just stops where it is.
    if (Math.abs(momentum) > flickMin) startMomentum()
  }

  // Capture phase, ahead of a card's overlay button: cancel the click a drag
  // would otherwise fire.
  function onClickCapture(event: MouseEvent) {
    if (!moved) return
    event.preventDefault()
    event.stopPropagation()
    moved = false
  }

  useEventListener(track, 'pointerdown', onDown)
  useEventListener(track, 'click', onClickCapture, { capture: true })
  // Target-less overload → window (SSR-safe: no-ops on the server). Tracking
  // move/up on the window keeps the drag alive when the cursor leaves the rail.
  useEventListener('pointermove', onMove)
  useEventListener('pointerup', onUp)
  useEventListener('pointercancel', onUp)

  return { dragging }
}
